import { morph, MORPH_OPTIONS } from "@theme/morph";

const MAX_CACHE_ENTRIES = 24;
const MAX_CACHE_BYTES = 2 * 1024 * 1024;

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  #cacheBytes = 0;

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener("load", this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM
   * @param {(node: Node | undefined) => string|number|undefined} [options.getNodeKey] - Custom node-identity key forwarded to the morph. When omitted, morph keys by `node.id` as before.
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode, mode = "full", getNodeKey } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);

      if (!abortController.signal.aborted) {
        const preparedSection = prepareSectionHTML(sectionId, sectionHTML);
        morphSection(sectionId, preparedSection, { mode, getNodeKey });
      }

      return sectionHTML;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(globalThis.location.href)) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise !== undefined) {
      return pendingPromise;
    }

    if (useCache) {
      const cachedHTML = this.#readCache(sectionUrl);

      if (cachedHTML) {
        return cachedHTML;
      }
    }

    pendingPromise = fetch(sectionUrl).then(response => {
      if (!response.ok) {
        throw new Error(`Section fetch failed: HTTP ${response.status}`);
      }
      return response.text();
    });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    try {
      const sectionHTML = await pendingPromise;
      if (useCache) {
        this.#writeCache(sectionUrl, sectionHTML);
      }
      return sectionHTML;
    } finally {
      // A failed request must not poison this URL forever. Identity checking
      // prevents an older request from deleting a newer in-flight promise.
      if (this.#pendingPromises.get(sectionUrl) === pendingPromise) {
        this.#pendingPromises.delete(sectionUrl);
      }
    }
  }

  /** @param {string} key @returns {string | null} */
  #readCache(key) {
    const value = this.#cache.get(key);
    if (value === undefined) {
      return null;
    }
    this.#cache.delete(key);
    this.#cache.set(key, value);
    return value;
  }

  /** @param {string} key @param {string} value */
  #writeCache(key, value) {
    const previous = this.#cache.get(key);
    if (previous !== undefined) {
      this.#cacheBytes -= previous.length * 2;
      this.#cache.delete(key);
    }

    this.#cache.set(key, value);
    this.#cacheBytes += value.length * 2;

    while (this.#cache.size > MAX_CACHE_ENTRIES || this.#cacheBytes > MAX_CACHE_BYTES) {
      const oldestKey = this.#cache.keys().next().value;
      if (oldestKey === undefined) {
        break;
      }
      const oldestValue = this.#cache.get(oldestKey) ?? "";
      this.#cache.delete(oldestKey);
      this.#cacheBytes -= oldestValue.length * 2;
    }
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll(".shopify-section")) {
      const url = buildSectionRenderingURL(section.id);

      if (!this.#cache.has(url) && !containsShadowRoot(section)) {
        this.#writeCache(url, section.outerHTML);
      }
    }
  }
}

const SECTION_ID_PREFIX = "shopify-section-";

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(globalThis.location.href)) {
  const sectionUrl = new URL(url);
  sectionUrl.searchParams.set("section_id", normalizeSectionId(sectionId));
  sectionUrl.searchParams.sort();

  return sectionUrl.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), "");
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return Boolean(element.shadowRoot) || [...element.children].some(child => containsShadowRoot(child));
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string | Document} html - The new markup, or a response parsed ahead of the commit
 * @param {Object} [options] - Additional options
 * @param {'hydration'|'full'} [options.mode] - Which parts of the section to morph into the DOM. 'hydration' will only morph nodes with `data-hydration-key` attributes.
 * @param {boolean} [options.injectStylesheet=false] - When true, extracts `style[data-section-stylesheet]` from the response and injects it into the section wrapper.
 * @param {(node: Node | undefined) => string|number|undefined} [options.getNodeKey] - Custom node-identity key forwarded to the morph. When omitted, morph keys by `node.id` as before.
 */
export function morphSection(sectionId, html, options = {}) {
  const { mode = "full", injectStylesheet = false, getNodeKey } = options;
  const fragment = typeof html === "string" ? prepareSectionHTML(sectionId, html) : html;
  const existingElement = /** @type {HTMLElement | null} */ (
    document.querySelector(`#${buildSectionSelector(sectionId)}`)
  );
  const newElement = /** @type {HTMLElement | null} */ (fragment.querySelector(`#${buildSectionSelector(sectionId)}`));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === "hydration",
    // Opt-in identity keying (e.g. cart rows keyed by their data-key). When the
    // caller passes no getNodeKey, morph falls back to node.id exactly as before
    // — purely additive, no behavior change for existing callers.
    ...(getNodeKey ? { getNodeKey } : {}),
  });

  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }

  // The morph preserves dynamically-assigned slot attributes (set by overflow-list
  // to move items between visible/overflow slots). After a morph, those stale slot
  // values prevent the overflow-list from recalculating which items fit. Dispatch a
  // reflow so it re-evaluates overflow from scratch.
  for (const overflowList of existingElement.querySelectorAll("overflow-list")) {
    overflowList.dispatchEvent(new CustomEvent("reflow", { detail: {} }));
  }
}

/**
 * Parse and validate a Section Rendering response before a visual transition
 * starts. This keeps fetch, text decoding, DOM parsing, and response validation
 * out of the View Transition update callback; the callback only performs the
 * synchronous morph and history commit.
 *
 * @param {string} sectionId
 * @param {string} html
 * @returns {Document}
 */
export function prepareSectionHTML(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, "text/html");
  if (!fragment.querySelector(`#${buildSectionSelector(sectionId)}`)) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }
  return fragment;
}

/**
 * Injects a `<style data-section-stylesheet>` from the parsed SFR response
 * into the live section wrapper. Replaces the existing stylesheet, if it exists.
 *
 * @param {Document} fragment - The parsed response document
 * @param {HTMLElement} sectionElement - The live section wrapper element
 */
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector("style[data-section-stylesheet]");
  if (!newStylesheet) {
    return;
  }

  const existingStylesheet = sectionElement.querySelector("style[data-section-stylesheet]");

  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}

export const sectionRenderer = new SectionRenderer();
